import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import { GUEST_PRIMARY_BUTTON_SX, GUEST_TEXT_BUTTON_SX } from './guestTableStyles';
import { selectionSummary } from '../../../lib/guestSelection';

/**
 * The bar that appears once rows are ticked.
 *
 * Sticky at the bottom because the roster is 137 rows and the host doing the
 * ticking is halfway down it — a bulk action parked above the table is a bulk
 * action that costs a scroll each time, and on a phone it is off screen
 * entirely.
 *
 * The second half of the sentence is the important half: when the filter is
 * hiding some of the ticked guests, it says so and says how many. A bulk write
 * that reaches people the host cannot see has to announce itself before the
 * click, and {@link selectionSummary} is where that sentence is decided.
 */
export default function GuestSelectionBar({
  visible,
  selected,
  disabled,
  onTag,
  onClear,
}: {
  visible: ReadonlyArray<{ inviteeId: string }>;
  selected: ReadonlySet<string>;
  disabled: boolean;
  onTag: () => void;
  onClear: () => void;
}) {
  if (selected.size === 0) return null;

  return (
    <Paper
      elevation={0}
      role="region"
      aria-label="Selected guests"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        p: 1.5,
        border: '1px solid var(--tsb-brand-border)',
        borderRadius: 'var(--tsb-radius-md)',
        bgcolor: 'var(--tsb-brand-soft)',
        backgroundImage: 'none',
      }}
    >
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ gap: 1 }}
      >
        <Typography sx={{ color: 'var(--tsb-fg)', fontSize: 13, fontWeight: 600 }}>
          {selectionSummary(visible, selected)}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          size="small"
          disabled={disabled}
          onClick={onTag}
          sx={GUEST_PRIMARY_BUTTON_SX}
        >
          Tag
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={onClear}
          sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
        >
          Clear
        </Button>
      </Stack>
    </Paper>
  );
}
